import { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, redirect, useLoaderData, useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import { reactionTypes } from '#app/components/ui/reactions.js'
import { buildComparator, dedupeByTemplate } from '#app/utils/build-popularity.js'
import { getBuildsByServer } from '#app/utils/builds.server.js'
import { momentsAgo } from '#app/utils/dateFormat.js'
import { cleanSkillName } from '#app/utils/scripts.helper.js'
import { getServerBySlug } from '#app/utils/shards.server.js'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    const serverDetails = data?.serverDetails;
    const title = `${data?.uniqueBuildCount} Unique UO Templates on ${data?.serverDetails.name} | UO Addicts`;
    const description = `Browse build templates for the Ultima Online server ${data?.serverDetails.name} on UO Addicts`;
    return [
    { title: title},
    { name: "description", content: description },
    { name: "keywords", content: `builds,templates,uo,ultima online,shard,server,${serverDetails?.name}`},
    { property: "og:image", content: `https://uoaddicts.com/images/logos/${serverDetails?.logo}`},
    { property: "og:image:alt", content: "Build details for Ultima Online on UO Addicts"},
    { property: "twitter:card", content: "summary"},
    { property: "twitter:creator", content: "@jaseowns"},
    { property: "twitter:site", content: "@jaseowns"},
    { property: "og:title", content: title},
    { property: "og:description", content: description},
]}

export async function loader({ request, params }: LoaderFunctionArgs) {
    const url = new URL(request.url)
    const sort = url.searchParams.get('sort') ?? 'popular'

    const serverDetails = await getServerBySlug({slug: params.serverSlug || ""})
    if (!serverDetails) {
        return redirect('/builds/new');
    }

    const {uniqueBuildCount, builds: rawBuilds} = await getBuildsByServer({serverId: serverDetails.id});

    // One card per template, sorted by popularity or last updated
    const comparator = buildComparator(sort)
    const builds = dedupeByTemplate(rawBuilds, comparator).sort(comparator)

    // Collect unique tags from the builds actually shown, so every filter hits
    const tagSet = new Set<string>()
    builds.forEach(b => b.tags.forEach(t => tagSet.add(t)))
    const allTags = Array.from(tagSet).sort()

    return json({
        serverDetails,
        uniqueBuildCount,
        builds,
        allTags,
        sort,
    });
};

export default function IndexRoute() {
  const { serverDetails, builds, uniqueBuildCount, allTags, sort } = useLoaderData<typeof loader>()
  const [searchParams, setSearchParams] = useSearchParams()
  const tagFilter = searchParams.get('tag') ?? ''
  const [localSearch, setLocalSearch] = useState(searchParams.get('q') ?? '')

  let filteredBuilds = builds

  // Text search (client-side since data is already loaded)
  const searchQuery = searchParams.get('q') ?? ''
  if (searchQuery) {
    const q = searchQuery.toLowerCase()
    filteredBuilds = filteredBuilds.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.description?.toLowerCase().includes(q) ?? false),
    )
  }

  // Tag filter
  if (tagFilter) {
    filteredBuilds = filteredBuilds.filter((b) =>
      b.tags.includes(tagFilter),
    )
  }

  const handleSort = (newSort: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('sort', newSort)
    setSearchParams(params)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams)
    if (localSearch) {
      params.set('q', localSearch)
    } else {
      params.delete('q')
    }
    setSearchParams(params)
  }

  const handleTagFilter = (tag: string) => {
    const params = new URLSearchParams(searchParams)
    if (tag === tagFilter) {
      params.delete('tag')
    } else {
      params.set('tag', tag)
    }
    setSearchParams(params)
  }

  const clearFilters = () => {
    setLocalSearch('')
    setSearchParams({})
  }

  const hasFilters = searchQuery || tagFilter

return (
	<div className="container mx-auto mb-24 mt-4 px-4 md:mb-48 md:mt-6">
		<Link to="/builds" className="text-sm text-muted-foreground underline hover:no-underline">
			&larr; All Builds
		</Link>

		<div className="mt-3 mb-4 md:mt-4 md:mb-6">
			<div className="flex items-center gap-3 md:gap-4">
				<img src={serverDetails.logo} alt={serverDetails.name} className="h-10 md:h-14" />
				<div>
					<h1 className="text-2xl font-bold md:text-4xl">{serverDetails.name} Builds</h1>
					<p className="text-xs text-muted-foreground md:text-sm">{builds.length} builds, {uniqueBuildCount} unique</p>
				</div>
			</div>
			<p className="mt-2 text-sm text-muted-foreground md:text-base">
				Have an idea for a build? <Link to={`/builds/${serverDetails.slug}/new`} className="underline hover:no-underline">Add yours on {serverDetails.name}</Link>
			</p>
		</div>

		{/* Search and filter controls */}
		<div className="mb-6 space-y-3 md:space-y-4">
			<form onSubmit={handleSearch} className="flex gap-2">
				<input
					type="text"
					value={localSearch}
					onChange={(e) => setLocalSearch(e.target.value)}
					placeholder="Search builds..."
					className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-10"
				/>
				<button
					type="submit"
					className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 md:px-4"
				>
					Search
				</button>
			</form>

			{allTags.length > 0 && (
				<div className="flex flex-wrap gap-1.5 md:gap-2">
					<span className="text-xs text-muted-foreground py-1 md:text-sm">Tags:</span>
					{allTags.map((tag) => (
						<button
							key={tag}
							onClick={() => handleTagFilter(tag)}
							className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors md:px-3 md:py-1 md:text-sm ${
								tagFilter === tag
									? 'bg-blue-600 text-white'
									: 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
							}`}
						>
							{tag}
						</button>
					))}
				</div>
			)}

			<div className="flex items-center gap-1.5 md:gap-2">
				<span className="text-xs text-muted-foreground md:text-sm">Sort:</span>
				<button
					onClick={() => handleSort('popular')}
					className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors md:px-3 md:py-1 md:text-sm ${
						sort === 'popular'
							? 'bg-blue-600 text-white'
							: 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
					}`}
				>
					Most Popular
				</button>
				<button
					onClick={() => handleSort('recent')}
					className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors md:px-3 md:py-1 md:text-sm ${
						sort === 'recent'
							? 'bg-blue-600 text-white'
							: 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
					}`}
				>
					Last Updated
				</button>
			</div>

			{hasFilters && (
				<div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
					<span className="text-muted-foreground">
						Showing {filteredBuilds.length} of {builds.length} builds
						{searchQuery && <> matching "<b>{searchQuery}</b>"</>}
						{tagFilter && <> tagged <b>{tagFilter}</b></>}
					</span>
					<button
						onClick={clearFilters}
						className="text-blue-600 underline hover:no-underline"
					>
						Clear filters
					</button>
				</div>
			)}
		</div>

		<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 md:gap-0">
        {
          filteredBuilds.map((build, index) => {

            let total = 0;
            const buildBreakout = build.build?.split(",").map(x => {
              const skill = x.split("|")
              const skillName = skill[0] ?? "No skill name"
              const skillValue = Number(skill[1]) ?? 0
              total += skillValue
              return { name: skillName, value: skillValue }
            })

            return <div key={`build_${index}`}>
                <Link to={`/builds/${build.server.slug}/${build.slug}`}>
                  <div className="skill-scroll-container">
                        <div className='m-2 md:m-4'>
                            <div className='text-center m-auto min-h-24 md:min-h-32 align-text-bottom'>
                            <img src={build.server.logo} alt={build.server.name} className="max-h-16 md:max-h-24 server-logo m-auto" />
                            <span className="text-xs md:text-sm">by {build.user.username} {momentsAgo(build.createdAt.toString())} ago</span>
                            {build.playerCount > 1 && (
                              <span className="block text-[10px] text-green-600 dark:text-green-400 md:text-xs">{build.playerCount} players use this build</span>
                            )}
                            </div>

                            <div className="skill-container-top relative">
                                <div className='not-input text-xs md:text-sm'>{build.name.length ? build.name : "No build name"}</div>
                                {build.reactionCount > 0 && (
                                    <div className="absolute top-1/2 right-1 -translate-y-1/2 flex gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white md:px-2 md:text-xs">
                                        {reactionTypes
                                            .filter((rt) => (build.reactionBreakdown[rt.type] ?? 0) > 0)
                                            .map((rt) => (
                                                <span key={rt.type} title={rt.label}>
                                                    {rt.icon}{build.reactionBreakdown[rt.type]}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </div>
                            <div className="skill-container skill-container-mid">
                                {buildBreakout?.sort( (a,b) => {
                                    if (a.name > b.name) {
                                        return 1;
                                    } else if (b.name > a.name) {
                                        return -1;
                                    }
                                    return 0;
                                }).map(x => { return <div className="skill-row" key={"selected_" + x.name}>
                                    <span className="name">{cleanSkillName(x.name)}</span> <span className="value">{x.value}</span>
                                    </div>})
                                }
                                <div className="skill-row totals">
                                    <span className="name">Total</span> <span className={total > 720 ? "value invalid" : "value"}>{total}</span>
                                </div>
                            </div>
                            <div className="skill-container-bot">&nbsp;</div>
                        </div>
                      </div>
                    </Link>
                    <div className='text-center mb-2 md:mb-0'>
                        {build.taggables.map((taggable) => { return <Link key={`tag_${taggable.tag.name}`} to={`/builds/tags/${taggable.tag.name}`} className="underline hover:no-underline text-xs md:text-sm m-1 md:m-2">{taggable.tag.name}</Link>})}
                    </div>
              </div>
          })
        }
		</div>

		{filteredBuilds.length === 0 && (
			<div className="py-12 text-center text-sm text-muted-foreground md:text-base">
				No builds found matching your filters. Try adjusting your search or <button onClick={clearFilters} className="underline hover:no-underline">clear all filters</button>.
			</div>
		)}
	</div>
);
}
